import { Dialog, Stack, TextField, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import React from 'react';

import { Enable2FAForm } from '@/components/molecules/Enable2FAForm';
import { QrCodeImage } from '@/components/molecules/QrCodeImage';
import { ApplicationModal } from '@/components/organisms/ApplicationModal';
import { TOTPResult } from '@/hooks/useTOTP';
import { useTranslator } from '@/hooks/useTranslator';

type SubmitCallback = (event: React.FormEvent) => void;

interface BaseProps {
  readonly open: boolean;
  readonly error?: string;
  readonly totp?: TOTPResult;

  readonly cancelButton: React.ReactElement;

  onCodeChange?(): void;
}

interface ActionProps extends BaseProps {
  readonly action: string;
  onSubmit?(event: React.FormEvent): void;
}

interface SubmitProps extends BaseProps {
  onSubmit(event: React.FormEvent): void;
}

type Props = SubmitProps | ActionProps;

const instructions = ['TOTP_STEP_1', 'TOTP_STEP_2', 'TOTP_STEP_3'];

export const Enable2FAModal: React.FC<Props> = (props: Props): React.ReactElement => {
  const [code, setCode] = React.useState<string>('');

  const { open, error, totp = emptyTotp, cancelButton = <></>, onCodeChange } = props;

  const tr = useTranslator();

  const handleOtpChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      const value = event.target.value.replace(/\D/g, '').slice(0, 6);
      onCodeChange?.();
      setCode(value);
    },
    [onCodeChange],
  );

  React.useEffect((): VoidFunction => {
    return (): void => {
      setCode('');
    };
  }, []);

  const action = React.useMemo((): string | undefined => {
    if (isActionProps(props)) {
      return props.action;
    }
  }, [props]);

  React.useEffect((): void => {
    if (!open) {
      setCode('');
    }
  }, [open]);

  const onSubmit = React.useMemo((): undefined | SubmitCallback => {
    if (isSubmitProps(props)) {
      return props.onSubmit;
    }
  }, [props]);

  return (
    <Dialog open={open} slotProps={dialogSlotProps}>
      <ApplicationModal
        title={tr('TWO_FACTOR_AUTHENTICATION')}
        description={tr('TWO_FACTOR_AUTHENTICATION_DESCRIPTION')}
        width={552}
        variant="prominent">
        <Box
          id="code-form"
          action={action}
          component="form"
          method="post"
          sx={styles.form}
          onSubmit={onSubmit}>
          <Box sx={styles.qrSection}>
            <Enable2FAForm action={action} totp={totp} />
            <QrCodeImage content={totp.uri} size={140} />
            <Box textAlign="center">
              <Typography variant="body2" color="custom.LightGray">
                {tr('CANT_SCAN_THE_CODE')}
              </Typography>
              <Typography variant="body2">
                <Typography variant="inherit" component="span" color="custom.LightGray">
                  {tr('USE_KEY')}
                  {': '}
                </Typography>
                <Typography variant="inherit" component="span">
                  {totp.secret}
                </Typography>
              </Typography>
            </Box>
          </Box>

          <Box display="grid" gridTemplateColumns="20px 1fr" my={3} rowGap={1.5}>
            {instructions.map(
              (instruction: string, index: number): React.ReactElement => (
                <React.Fragment key={instruction}>
                  <Typography variant="body2">{index + 1}. </Typography>
                  <Typography variant="body2">{tr(instruction)}</Typography>
                </React.Fragment>
              ),
            )}
          </Box>

          <Box sx={styles.inputSection}>
            <TextField
              name={totp.secret}
              label={tr('ENTER_TOTP_CODE')}
              fullWidth={true}
              value={code}
              error={Boolean(error)}
              helperText={error}
              onChange={handleOtpChange}
            />
          </Box>
        </Box>
        <Stack alignItems="center" mt={2} gap={2}>
          <Button
            type="submit"
            variant="contained"
            form="code-form"
            fullWidth={true}
            disabled={code.length !== 6}>
            {tr('VERIFY')}
          </Button>

          {cancelButton}
        </Stack>
      </ApplicationModal>
    </Dialog>
  );
};

const styles: Record<string, SystemStyleObject> = {
  form: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  qrSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  inputSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    width: '100%',
  },
};

const dialogSlotProps = {
  paper: {
    sx: {
      minWidth: 0,
      padding: 0,
    },
  },
};

const emptyTotp: TOTPResult = {
  algorithm: 'SHA1',
  digits: 6,
  period: 30,
  secret: '',
  uri: '',
  validate(_: string): boolean {
    return false;
  },
};

const isActionProps = (props: Props): props is ActionProps => {
  return 'action' in props;
};

const isSubmitProps = (props: Props): props is SubmitProps => {
  return 'onSubmit' in props;
};
